"use server";

import { apiClient, ApiClientError } from "@/lib/api/client";
import { getImperialSession } from "@/lib/api/session";
import { revalidateCmsPageCache } from "@/lib/admin/cache";

/**
 * Server Action — reset podstrony do template_default. Kasuje WSZYSTKIE
 * per-page wiersze z `imp_cms_catalog_layouts` + `imp_cms_catalog_window` +
 * `imp_cms_catalog_area_state` dla `idCatalog`. Po reset'cie cascade
 * fallback'uje do template_default (`is_global_default=1`).
 *
 * **Frontend guard:** button widoczny tylko gdy `defaultTemplateExists=true`
 * (z meta backend'u). Backend NIE waliduje istnienia template'u — operacja
 * jest dozwolona zawsze (admin może świadomie wyczyścić wszystko).
 *
 * **Auth:** Bearer + scope `cms:manage_windows`.
 */
export async function resetToDefaultsAction(input: {
  idCatalog: number | null;
  lang: string;
  slug: string;
}): Promise<{
  ok: boolean;
  error?: string;
  deletedLayouts?: number;
  deletedWindows?: number;
  deletedAreas?: number;
  totalDeleted?: number;
}> {
  const session = await getImperialSession();
  if (!session) {
    return { ok: false, error: "unauthenticated" };
  }

  try {
    const response = await apiClient<{
      data: {
        deleted_layouts: number;
        deleted_windows: number;
        deleted_areas: number;
        total_deleted: number;
      };
      meta: { id_catalog: number | null };
    }>("/IRESTAPI/v1/IWINDOWS_MANAGER_CONTROLLER/resetToDefaults", {
      method: "POST",
      bearerToken: session.accessToken,
      lang: input.lang,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id_catalog: input.idCatalog,
      }),
    });

    await revalidateCmsPageCache(input.slug);
    return {
      ok: true,
      deletedLayouts: response.data.deleted_layouts,
      deletedWindows: response.data.deleted_windows,
      deletedAreas: response.data.deleted_areas,
      totalDeleted: response.data.total_deleted,
    };
  } catch (e) {
    if (e instanceof ApiClientError) {
      console.warn(`[resetToDefaultsAction] failed: ${e.message}`);
      return { ok: false, error: e.message };
    }
    return { ok: false, error: "unexpected_error" };
  }
}

/**
 * Server Action — upsert flagi enabled/disabled dla obszaru (`area_key`) w
 * danym scope (`type`, `idCatalog`, `requestUri`).
 *
 * **Backend SoT pattern:** wszystkie zmiany lecą do `imp_cms_catalog_area_state`
 * przez `POST /cms/areas/state` (handler `IAPI_saveAreaState`). Cascade per
 * area_key niezależnie — najwyższy tier z explicit wpisem wygrywa, brak wpisu
 * w żadnym tier = enabled (default).
 *
 * **Scope mapping** (z `resolveSaveTarget()` w AdminContext):
 *   - homepage -> type=0, idCatalog=null
 *   - catalog page -> type=1, idCatalog=X
 *   - article + saveScope='this_url' -> type=3, idCatalog=X, requestUri=Y
 *   - article + saveScope='all_articles' -> type=2, idCatalog=X
 *   - article + saveScope='catalog' -> type=1, idCatalog=X
 *
 * **Guard:** backend odrzuca disable dla obszarów `is_catalog_area=1`
 * (np. `catalogContent`) — wyłączenie ukryłoby główną treść podstrony.
 * Frontend powinien ukrywać toggle dla takich obszarów żeby user nie dostawał
 * błędu (lookup w `imp_cms_layout_areas` per current layout area metadata).
 *
 * **Auth:** wymaga Bearer token z SSO + scope `cms:manage_windows`.
 */
export async function saveAreaStateAction(input: {
  areaKey: string;
  enabled: boolean;
  type: 0 | 1 | 2 | 3;
  idCatalog: number | null;
  requestUri: string | null;
  /** Locale ISO ('pl', 'en', 'de') — backend mapuje na id_domain_language. */
  lang: string;
  /** Slug do revalidatePath po sukcesie. */
  slug: string;
}): Promise<{ ok: boolean; error?: string }> {
  const session = await getImperialSession();
  if (!session) {
    return { ok: false, error: "unauthenticated" };
  }

  try {
    const body = {
      area_key: input.areaKey,
      type: input.type,
      id_catalog: input.idCatalog,
      request_uri: input.requestUri,
      enabled: input.enabled,
    };
    await apiClient<{
      data: { area_key: string; enabled: boolean };
      meta: { type: number; id_catalog: number | null; request_uri: string | null };
    }>("/IRESTAPI/v1/IWINDOWS_MANAGER_CONTROLLER/saveAreaState", {
      method: "POST",
      bearerToken: session.accessToken,
      lang: input.lang,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    await revalidateCmsPageCache(input.slug);
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiClientError) {
      return { ok: false, error: e.message };
    }
    return { ok: false, error: "unexpected_error" };
  }
}
